import * as React from 'react'
import { Link, navigate } from 'gatsby'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Popover from '@mui/material/Popover'
import Avatar from '@mui/material/Avatar'
import { useLocation } from '@reach/router'
import { PAGE_INFO, MENU_ITEMS, MENU_LANDING_ITEMS } from '../utils/menu-items'
import LoginIcon from '@mui/icons-material/Login'
import logo from '../images/icon.png'
import Login from './forms/login'
import { auth } from '../../firebase-info'
import {
    getAvatarColorFromLetter,
    getHumanizedNeighbourRoles,
    getNeighbourIcon,
    requestNotificationPermission,
} from '../utils/functions'
import { Badge } from '@mui/material'

function samePageLinkNavigation(event) {
    return !(
        event.defaultPrevented ||
        event.button !== 0 || // ignore everything but left-click
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey
    )
}

export default function Header({ community, loggedUser, neighbour }) {
    const [open, setOpen] = React.useState(false)
    const [value, setValue] = React.useState(false)
    const [landingValue, setLandingValue] = React.useState(false)
    const [anchorEl, setAnchorEl] = React.useState(null)

    const openPopover = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const closePopover = () => {
        setAnchorEl(null)
    }

    const popoverOpen = Boolean(anchorEl)
    const id = popoverOpen ? 'simple-popover' : undefined

    const handleChange = (event, newValue) => {
        if (
            event.type !== 'click' ||
            (event.type === 'click' && samePageLinkNavigation(event))
        ) {
            requestNotificationPermission()
            setValue(newValue)
            setLandingValue(false)
        }
    }

    const handleLandingValue = (event, newValue) => {
        if (
            event.type !== 'click' ||
            (event.type === 'click' && samePageLinkNavigation(event))
        ) {
            setLandingValue(newValue)
            setValue(false)
        }
    }

    const location = useLocation()

    React.useEffect(() => {
        const key =
            Object.keys(PAGE_INFO).find((el) =>
                location.pathname.includes(el)
            ) || '/'

        if (MENU_ITEMS.includes(key)) {
            setValue(PAGE_INFO[key].menu)
            setLandingValue(false)
        } else if (MENU_LANDING_ITEMS.includes(key)) {
            setLandingValue(PAGE_INFO[key].menu)
            setValue(false)
        } else {
            setValue(false)
            setLandingValue(false)
        }
    }, [location.pathname])

    const getHeaderTitle = () => {
        const key = Object.keys(PAGE_INFO).find((el) =>
            location.pathname.includes(el)
        )

        return PAGE_INFO[key]?.title || ''
    }

    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen)
    }

    const userFirstLetter = loggedUser?.name?.at(0).toUpperCase() || '?'
    const avatarColor = getAvatarColorFromLetter(userFirstLetter)

    const propertiesNumber = neighbour?.properties.length
        ? neighbour?.properties.length
        : 'Ninguna'
    const propertiesPlural = neighbour?.properties.length <= 1 ? '' : 'es'

    const DrawerList = (
        <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={toggleDrawer(false)}
        >
            <List>
                <ListItem key={'helloMessage'}>
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ marginRight: '32px' }}
                    >
                        {`¡Hola${loggedUser ? ', ' + loggedUser.name : ''}!`}
                    </Typography>
                </ListItem>
                {MENU_ITEMS.map((item) => (
                    <ListItem key={item} disablePadding>
                        <ListItemButton
                            onClick={() => {
                                requestNotificationPermission()
                                navigate(item)
                            }}
                        >
                            <ListItemIcon>{PAGE_INFO[item].icon}</ListItemIcon>
                            <ListItemText primary={PAGE_INFO[item].title} />
                        </ListItemButton>
                    </ListItem>
                ))}
                <ListItem key={'logout'} disablePadding>
                    <ListItemButton
                        onClick={() => {
                            closePopover()
                            auth.signOut()
                            navigate('/')
                        }}
                    >
                        <ListItemIcon>
                            <LogoutRoundedIcon />
                        </ListItemIcon>
                        <ListItemText primary={'Cerrar sesión'} />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    )

    return (
        <AppBar position="sticky">
            <Toolbar sx={{ display: 'flex', gap: 2 }}>
                {loggedUser ? (
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        sx={{ display: { xs: 'block', md: 'none' } }}
                        onClick={toggleDrawer(true)}
                    >
                        <MenuIcon />
                    </IconButton>
                ) : null}
                <Box sx={{ mt: '8px' }}>
                    <Link to={'/'}>
                        <img width="50" height="50" src={logo} alt="Convive" />
                    </Link>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: { sx: 'flex-start', md: 'center' },
                        gap: { xs: 0, md: 2 },
                        flexDirection: { xs: 'column', md: 'row' },
                    }}
                >
                    <Typography variant="h6" component="h6">
                        {getHeaderTitle()}
                    </Typography>
                    {community ? (
                        <Typography
                            variant="p"
                            component="p"
                            sx={{ cursor: 'pointer' }}
                            onClick={() =>
                                navigate('/neighbour/community/home/')
                            }
                        >
                            {community}
                        </Typography>
                    ) : null}
                </Box>
                <Tabs
                    value={landingValue}
                    onChange={handleLandingValue}
                    textColor={'inherit'}
                    indicatorColor="secondary"
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="nav tabs for application"
                    role="navigation"
                >
                    {MENU_LANDING_ITEMS.map((item) => (
                        <Tab
                            component={Link}
                            icon={PAGE_INFO[item].icon}
                            iconPosition="start"
                            label={PAGE_INFO[item].title}
                            key={item}
                            to={item}
                        />
                    ))}
                </Tabs>
                <Box sx={{ display: 'flex', marginLeft: 'auto' }}>
                    {!loggedUser ? (
                        <React.Fragment>
                            <Button
                                color="inherit"
                                endIcon={<LoginIcon />}
                                sx={{ display: { xs: 'none', md: 'flex' } }}
                                onClick={openPopover}
                            >
                                Iniciar sesión
                            </Button>
                            <Button
                                color="inherit"
                                size="large"
                                endIcon={<LoginIcon />}
                                sx={{ display: { xs: 'flex', md: 'none' } }}
                                onClick={openPopover}
                            />
                            <Popover
                                id={id}
                                open={popoverOpen}
                                anchorEl={anchorEl}
                                onClose={closePopover}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <Box sx={{ minWidth: '400px' }}>
                                    <Login closePopover={closePopover} />
                                </Box>
                            </Popover>
                        </React.Fragment>
                    ) : (
                        <>
                            {neighbour &&
                            process.env.NODE_ENV === 'development' ? (
                                <Badge
                                    overlap="circular"
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    badgeContent={getNeighbourIcon(neighbour, {
                                        fontSize: '18px',
                                    })}
                                >
                                    <Avatar
                                        alt={loggedUser.name}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: avatarColor,
                                        }}
                                        onClick={openPopover}
                                    >
                                        {userFirstLetter}
                                    </Avatar>
                                </Badge>
                            ) : (
                                <Avatar
                                    alt={loggedUser.name}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: avatarColor,
                                    }}
                                    onClick={openPopover}
                                >
                                    {userFirstLetter}
                                </Avatar>
                            )}
                            <Popover
                                id={id}
                                open={popoverOpen}
                                anchorEl={anchorEl}
                                onClose={closePopover}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <Box
                                    m={2}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                    }}
                                >
                                    <Avatar
                                        alt={loggedUser.name}
                                        sx={{
                                            bgcolor: avatarColor,
                                            width: 60,
                                            height: 60,
                                            fontSize: 30,
                                        }}
                                    >
                                        {userFirstLetter}
                                    </Avatar>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant={'h6'}
                                            noWrap
                                            fontWeight={'bold'}
                                            color="primary"
                                        >
                                            {loggedUser.name}
                                        </Typography>
                                        <Typography
                                            mb={1}
                                            noWrap
                                            color="primary"
                                        >
                                            {loggedUser.email}
                                        </Typography>
                                        <Typography noWrap color="primary">
                                            {getHumanizedNeighbourRoles(
                                                neighbour
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >{`${propertiesNumber} propiedad${propertiesPlural} en esta comunidad`}</Typography>
                                        {process.env.NODE_ENV ===
                                        'development' ? (
                                            <Box
                                                m={1}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="primary"
                                                    noWrap
                                                >{`User ID: ${loggedUser?.id}`}</Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="primary"
                                                    noWrap
                                                >{`Neighbour ID: ${neighbour?.id}`}</Typography>
                                            </Box>
                                        ) : null}
                                    </Box>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => {
                                            closePopover()
                                            navigate('/neighbour/user-profile/')
                                        }}
                                        startIcon={<AccountCircleRoundedIcon />}
                                    >
                                        Mi Perfil
                                    </Button>
                                    <Button
                                        fullWidth
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => {
                                            closePopover()
                                            auth.signOut()
                                            navigate('/')
                                        }}
                                        startIcon={<LogoutRoundedIcon />}
                                    >
                                        Cerrar Sesión
                                    </Button>
                                </Box>
                            </Popover>
                        </>
                    )}
                </Box>
            </Toolbar>
            {loggedUser ? (
                <>
                    <Toolbar sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            textColor={'inherit'}
                            indicatorColor="secondary"
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="nav tabs for application"
                            role="navigation"
                        >
                            {MENU_ITEMS.map((item) => (
                                <Tab
                                    component={Link}
                                    icon={PAGE_INFO[item].icon}
                                    iconPosition="start"
                                    label={PAGE_INFO[item].title}
                                    key={item}
                                    to={item}
                                />
                            ))}
                        </Tabs>
                    </Toolbar>
                    <Drawer open={open} onClose={toggleDrawer(false)}>
                        {DrawerList}
                    </Drawer>
                </>
            ) : null}
        </AppBar>
    )
}
